import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { MediaImage } from '@/components/ui/media-image';
import { resolveBundledMedia } from '@/lib/media-fallback';

const DEFAULT_ROTATION_MS = 6000;
const HERO_CACHE_KEY = 'public-hero-config-v1';

interface HeroSlide {
  mobile: string;
  desktop: string;
  description?: string;
}

interface HeroConfig {
  slides: HeroSlide[];
  rotationIntervalMs: number;
  version: string;
}

type HeroFetchResult =
  | { kind: 'config'; config: HeroConfig }
  | { kind: 'empty' }
  | { kind: 'error' };

function isValidHeroSlide(value: unknown): value is HeroSlide {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as HeroSlide).mobile === 'string' &&
    (value as HeroSlide).mobile.trim().length > 0 &&
    typeof (value as HeroSlide).desktop === 'string' &&
    (value as HeroSlide).desktop.trim().length > 0
  );
}

function normalizeHeroConfig(raw: Record<string, unknown>): HeroConfig | null {
  const rawSlides = Array.isArray(raw.slides) ? raw.slides.filter(isValidHeroSlide) : [];
  const version = typeof raw.updatedAt === 'string' && raw.updatedAt.trim().length > 0
    ? raw.updatedAt
    : 'unversioned';
  const slides = rawSlides.map((slide) => ({
    ...slide,
    mobile: appendCacheVersion(slide.mobile, version),
    desktop: appendCacheVersion(slide.desktop, version),
  }));
  if (slides.length === 0) return null;

  return {
    slides,
    rotationIntervalMs: typeof raw.rotationIntervalMs === 'number' ? raw.rotationIntervalMs : DEFAULT_ROTATION_MS,
    version,
  };
}

function appendCacheVersion(url: string, version: string): string {
  try {
    const resolved = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    resolved.searchParams.set('v', version);
    return resolved.toString();
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${encodeURIComponent(version)}`;
  }
}

function readCachedHeroConfig(): HeroConfig | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(HERO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return normalizeHeroConfig(parsed);
  } catch {
    return null;
  }
}

function writeCachedHeroConfig(config: HeroConfig): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(HERO_CACHE_KEY, JSON.stringify(config));
  } catch {
    // Ignore storage failures.
  }
}

function clearCachedHeroConfig(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(HERO_CACHE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function preloadHeroImages(slides: HeroSlide[], currentIndex = 0): void {
  if (typeof window === 'undefined') return;

  if (slides.length === 0) return;

  const indicesToPreload = new Set<number>([currentIndex]);
  if (slides.length > 1) {
    indicesToPreload.add((currentIndex + 1) % slides.length);
  }

  for (const index of indicesToPreload) {
    const slide = slides[index];
    if (!slide) continue;

    for (const src of [slide.mobile, slide.desktop]) {
      const resolved = resolveBundledMedia(src) ?? src;
      const image = new Image();
      image.decoding = 'async';
      image.src = resolved;
    }
  }
}

async function fetchHeroConfig(): Promise<HeroFetchResult> {
  try {
    const response = await api.get<{ hero: Record<string, unknown> | null }>('api/site-content/hero', {
      skipAuth: true,
      timeout: 3_000,
    });
    if (response.success && response.data?.hero) {
      const config = normalizeHeroConfig(response.data.hero);
      return config ? { kind: 'config', config } : { kind: 'empty' };
    }
    return { kind: 'empty' };
  } catch {
    return { kind: 'error' };
  }
}

export function HeroSection() {
  const [slides, setSlides] = useState<HeroSlide[]>(() => readCachedHeroConfig()?.slides ?? []);
  const [rotationMs, setRotationMs] = useState(DEFAULT_ROTATION_MS);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [showcaseOpen, setShowcaseOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const cached = readCachedHeroConfig();
    if (cached) {
      setSlides(cached.slides);
      setRotationMs(cached.rotationIntervalMs);
      preloadHeroImages(cached.slides, 0);
    }

    fetchHeroConfig().then((result) => {
      if (cancelled) return;

      if (result.kind === 'config') {
        setSlides(result.config.slides);
        setRotationMs(result.config.rotationIntervalMs);
        writeCachedHeroConfig(result.config);
        preloadHeroImages(result.config.slides, 0);
        return;
      }

      if (result.kind === 'empty') {
        clearCachedHeroConfig();
        setSlides([]);
      }
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setMounted(true);
    if (slides.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, rotationMs);
    return () => clearInterval(interval);
  }, [rotationMs, slides.length]);

  useEffect(() => {
    preloadHeroImages(slides, currentSlide);
  }, [currentSlide, slides]);

  if (slides.length === 0) {
    return (
      <section className="relative overflow-hidden bg-background py-24 text-foreground">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-0 h-80 w-[44rem] -translate-x-1/2 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
        </div>

        <div className="container relative mx-auto px-4">
          <div className="mx-auto max-w-4xl text-center">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-primary">
              Dominion Mandate
            </p>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
              Raising Leaders That Transform Society
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
              Connect with live worship, powerful teaching, and the Dominion Mandate community
              from anywhere while managed hero media is loading.
            </p>

            <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
              <Button size="lg" className="group" asChild>
                <Link to="/about">
                  About Dr. David
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="group" asChild>
                <Link to="/watch">
                  Watch Live
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const current = slides[currentSlide] || slides[0];

  return (
    <section className="relative pt-16 md:pt-20 lg:pt-24 -mt-16 text-foreground">
      <div className="container mx-auto px-4">

        {/* Mobile: CTA Above — 11:16 portrait */}
        <div className="flex md:hidden flex-col items-center gap-6 mb-6">
          <div className="max-[430px]:flex hidden flex-col items-center gap-1.5 pt-2 text-center">
            <p className="text-base font-bold uppercase tracking-[0.18em] text-primary">
              Dominion Mandate
            </p>
            <p className="text-base text-muted-foreground">
              Raising leaders that transform society
            </p>
          </div>
          <div className="mt-4 flex w-full max-w-[260px] flex-col gap-3">
            <Button size="lg" className="group w-full py-7" asChild>
              <Link to="/about">
                About Dr. David
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="group w-full" asChild>
              <Link to="/watch">
                Watch Live
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>

          <div
            onClick={() => setShowcaseOpen(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setShowcaseOpen(true);
              }
            }}
            className="relative w-full max-w-[340px] overflow-hidden rounded-xl shadow-2xl shadow-black/50 cursor-zoom-in"
            style={{ aspectRatio: '11 / 16' }}
            role="button"
            tabIndex={0}
            aria-label="Open prompt showcase carousel"
          >
            {slides.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
                  }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                  aria-label="Previous slide"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setCurrentSlide((prev) => (prev + 1) % slides.length);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                  aria-label="Next slide"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                </button>
              </>
            )}
            {slides.map((slide, index) => (
              <div
                key={slide.mobile}
                className={cn(
                  "absolute inset-0 transition-opacity duration-1000 ease-in-out",
                  mounted && index === currentSlide ? "opacity-100" : "opacity-0"
                )}
              >
                <MediaImage
                  src={slide.mobile}
                  alt=""
                  className="object-cover w-full h-full"
                  loading={index === currentSlide ? 'eager' : 'lazy'}
                  fetchPriority={index === currentSlide ? 'high' : 'low'}
                  decoding="async"
                />
              </div>
            ))}
          </div>

          {slides.length > 1 && (
            <div className="flex gap-2">
              {slides.map((_, i) => (
                <div
                  key={i}
                  className={cn("w-2 h-2 rounded-full transition-colors", i === currentSlide ? "bg-accent" : "bg-muted-foreground/30")}
                />
              ))}
            </div>
          )}

          {current.description && (
            <p className="text-center text-muted-foreground text-sm italic">{current.description}</p>
          )}
        </div>

        {/* Desktop: Images Above — 16:9 landscape, CTA Below */}
        <div className="hidden md:flex flex-col items-center gap-8">
          <div
            onClick={() => setShowcaseOpen(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setShowcaseOpen(true);
              }
            }}
            className="relative w-full max-w-5xl aspect-video overflow-hidden rounded-xl shadow-2xl shadow-black/50 cursor-zoom-in"
            role="button"
            tabIndex={0}
            aria-label="Open prompt showcase carousel"
          >
            {slides.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                  aria-label="Previous slide"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setCurrentSlide((prev) => (prev + 1) % slides.length);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                  aria-label="Next slide"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                </button>
              </>
            )}
            {slides.map((slide, index) => (
              <div
                key={slide.desktop}
                className={cn(
                  "absolute inset-0 transition-opacity duration-1000 ease-in-out",
                  mounted && index === currentSlide ? "opacity-100" : "opacity-0"
                )}
              >
                <MediaImage
                  src={slide.desktop}
                  alt=""
                  className="object-cover w-full h-full"
                  loading={index === currentSlide ? 'eager' : 'lazy'}
                  fetchPriority={index === currentSlide ? 'high' : 'low'}
                  decoding="async"
                />
              </div>
            ))}
          </div>

          {slides.length > 1 && (
            <div className="flex gap-2">
              {slides.map((_, i) => (
                <div
                  key={i}
                  className={cn("w-2 h-2 rounded-full transition-colors", i === currentSlide ? "bg-accent" : "bg-muted-foreground/30")}
                />
              ))}
            </div>
          )}

          {current.description && (
            <p className="text-center text-muted-foreground text-sm italic">{current.description}</p>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center pb-6">
            <Button size="lg" className="group" asChild>
              <Link to="/about">
                About Dr. David
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="group" asChild>
              <Link to="/watch">
                Watch Live
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={showcaseOpen} onOpenChange={setShowcaseOpen}>
        <DialogContent className="max-w-[min(96vw,1100px)] border-border/60 bg-background/96 p-3 sm:p-4">
          <DialogHeader className="sr-only">
            <DialogTitle>Prompt Showcase Carousel</DialogTitle>
            <DialogDescription>
              Browse the hero image showcase in an expanded carousel view.
            </DialogDescription>
          </DialogHeader>
          <div className="relative overflow-hidden rounded-xl bg-black/90">
            <div className="relative aspect-[11/16] w-full md:aspect-video">
              {slides.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)}
                    className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white transition-colors hover:bg-black/75"
                    aria-label="Previous showcase slide"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentSlide((prev) => (prev + 1) % slides.length)}
                    className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white transition-colors hover:bg-black/75"
                    aria-label="Next showcase slide"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                  </button>
                </>
              )}
              {slides.map((slide, index) => (
                <div
                  key={`showcase-${slide.desktop}-${index}`}
                  className={cn(
                    "absolute inset-0 transition-opacity duration-500 ease-in-out",
                    index === currentSlide ? "opacity-100" : "opacity-0"
                  )}
                >
                  <picture>
                    <source media="(min-width: 768px)" srcSet={resolveBundledMedia(slide.desktop) ?? slide.desktop} />
                    <MediaImage
                      src={slide.mobile}
                      alt={slide.description || `Hero showcase slide ${index + 1}`}
                      className="h-full w-full object-contain"
                    />
                  </picture>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between gap-4 px-1">
            <p className="min-h-5 text-sm text-muted-foreground">
              {current.description || 'Tap through the current hero showcase slides.'}
            </p>
            {slides.length > 1 && (
              <div className="flex gap-2">
                {slides.map((_, i) => (
                  <button
                    key={`showcase-dot-${i}`}
                    type="button"
                    onClick={() => setCurrentSlide(i)}
                    className={cn(
                      "h-2.5 w-2.5 rounded-full transition-colors",
                      i === currentSlide ? "bg-primary" : "bg-muted-foreground/30"
                    )}
                    aria-label={`Go to showcase slide ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
